import { IFilterBarData } from '@/components/basicComponents/filterBar';
import { ISort } from '@/components/table/types';
import { useSearchParams } from 'react-router-dom';

export const useSearchParamsManager = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const onChangeActiveTab = (newActiveTab: string, prevActiveTab: string, additionalParams: Record<string, any> = {}) => {
    if (newActiveTab === prevActiveTab) return;
    setSearchParams({ ...additionalParams, activeTab: newActiveTab }, { replace: true });
  };

  const onChangeSort = (value: ISort) => {
    setSearchParams(
      (prev) => {
        prev.set('key', value.key);
        prev.set('asc', value.asc.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeSearch = (searchValue: string) => {
    setSearchParams(
      (prev) => {
        if (!searchValue) {
          prev.delete('q');
          return prev;
        }
        prev.set('q', searchValue);
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeFilterBar = (filterBarItems: IFilterBarData[]) => {
    const filterBarValues = filterBarItems.filter((item) => item.enabled).map((item) => item.value);
    setSearchParams(
      (prev) => {
        if (!filterBarValues.length) {
          prev.delete('filterBar');
          return prev;
        }
        prev.set('filterBar', JSON.stringify(filterBarValues));
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangePrimaryFilter = (filter: string, filterParamName?: string) => {
    setSearchParams(
      (prev) => {
        !!filter ? prev.set(filterParamName || 'primaryFilter', filter) : prev.delete(filterParamName || 'primaryFilter');
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangePaginationData = (limit: number, offset: number) => {
    setSearchParams(
      (prev) => {
        prev.set('limit', limit.toString());
        prev.set('offset', offset.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeToggle = (toggle: boolean, { shouldSaveScrollPosition = true }: { shouldSaveScrollPosition?: boolean } = {}) => {
    setSearchParams(
      (prev) => {
        prev.set('toggle', Boolean(!toggle).toString());
        return prev;
      },
      { replace: true }
    );
    if (shouldSaveScrollPosition) {
      saveScrollPostion();
    }
  };

  const saveScrollPostion = () => {
    setSearchParams(
      (prev) => {
        prev.set('position', window.scrollY.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const makeScroll = (position: string | number) => {
    if (!position) return;
    setTimeout(() => window.scrollTo({ top: Number(position), behavior: 'smooth' }), 0);
    setSearchParams(
      (prev) => {
        prev.delete('position');
        return prev;
      },
      { replace: true }
    );
  };

  return {
    searchParams,
    setSearchParams,
    onChangeActiveTab,
    onChangeSort,
    onChangeSearch,
    onChangeFilterBar,
    onChangePrimaryFilter,
    onChangePaginationData,
    onChangeToggle,
    saveScrollPostion,
    makeScroll
  };
};
